import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/components/Navbar.css';

// Import your logo
import companyLogo from '../assets/images/logo/companylogo.png';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [mobileServicesDropdown, setMobileServicesDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPageChanging, setIsPageChanging] = useState(false);
  
  const dropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const navbarRef = useRef(null);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setServicesDropdown(false);
    setMobileServicesDropdown(false);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdown(false);
      }
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
        setMobileServicesDropdown(false);
      }
      if (navbarRef.current && !navbarRef.current.contains(event.target) && isMenuOpen) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Handle escape key and body overflow
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Enhanced animation variants
  const menuVariants = {
    closed: {
      x: '100%',
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 40,
        when: "afterChildren"
      }
    },
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 40,
        staggerChildren: 0.07,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    closed: {
      x: 50,
      opacity: 0,
      transition: {
        duration: 0.2
      }
    },
    open: {
      x: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 30
      }
    }
  };

  const overlayVariants = {
    closed: {
      opacity: 0,
      transition: {
        duration: 0.3
      }
    },
    open: {
      opacity: 1,
      transition: {
        duration: 0.3
      }
    }
  };

  const dropdownVariants = {
    closed: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    },
    open: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const mobileDropdownVariants = {
    closed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3
      }
    },
    open: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  // Enhanced hamburger animations
  const hamburgerVariants = {
    closed: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 17
      }
    },
    open: {
      scale: 1.1,
      rotate: 180,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 17
      }
    }
  };

  const line1Variants = {
    closed: { 
      rotate: 0, 
      y: 0,
      background: "white"
    },
    open: { 
      rotate: 45, 
      y: 8,
      background: "var(--primary)"
    }
  };

  const line2Variants = {
    closed: { 
      opacity: 1,
      scale: 1
    },
    open: { 
      opacity: 0,
      scale: 0
    }
  };

  const line3Variants = {
    closed: { 
      rotate: 0, 
      y: 0,
      background: "white"
    },
    open: { 
      rotate: -45, 
      y: -8,
      background: "var(--primary)"
    }
  };

  // Page transition animations for logo and company name
  const logoVariants = {
    initial: {
      y: -50,
      opacity: 0,
      scale: 0.8,
      rotate: -10
    },
    animate: {
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        duration: 0.6
      }
    },
    exit: {
      y: -50,
      opacity: 0,
      scale: 0.8,
      rotate: 10,
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    }
  };

  const companyNameVariants = {
    initial: {
      y: -30,
      opacity: 0,
      x: -20
    },
    animate: {
      y: 0,
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25,
        duration: 0.7,
        delay: 0.1
      }
    },
    exit: {
      y: -30,
      opacity: 0,
      x: 20,
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    }
  };

  const brandContainerVariants = {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    },
    exit: {
      opacity: 0,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };

  const navItems = [
    { name: 'Home', path: '/', icon: '🏠' },
    { name: 'About', path: '/about', icon: '👥' },
    { 
      name: 'Services', 
      path: '/services', 
      icon: '🛠️',
      submenu: [
        { name: 'Residential Construction', path: '/services', icon: '🏠' },
        { name: 'Renovation & Remodeling', path: '/architectural-design', icon: '🔨' },
        { name: 'Commercial Construction', path: '/services', icon: '🏢' },
        { name: 'Industrial Construction', path: '/services', icon: '🏭' },
        { name: 'Religious Buildings', path: '/services', icon: '⛪' },
        { name: 'Roads & Infrastructure', path: '/services', icon: '🛣️' },
        { name: 'Educational Facilities', path: '/services', icon: '🏫' },
      ]
    },
    { name: 'Projects', path: '/projects', icon: '📦' },
    { name: 'Blog', path: '/blog', icon: '📝' },
    { name: 'CEO Message', path: '/ceo-message', icon: '💼' },
    { name: 'Cost-Price', path: '/construction-cost', icon: '💲' },
    { name: 'Contact', path: '/contact', icon: '📞' }
  ];

  const handleServicesClick = (e, isMobile = false) => {
    e.preventDefault();
    if (isMobile) {
      setMobileServicesDropdown(!mobileServicesDropdown);
    } else {
      setServicesDropdown(!servicesDropdown);
    }
  };

  const handleSubmenuClick = (path) => {
    closeMenu();
    // Trigger page transition animation
    handlePageChange();
    // You can add navigation logic here if using React Router
    setTimeout(() => {
      window.location.href = path;
    }, 600);
  };

  const handleNavLinkClick = (e, item) => {
    if (item.submenu && isMobile) {
      e.preventDefault();
      handleServicesClick(e, true);
    } else if (!item.submenu) {
      e.preventDefault();
      handlePageChange();
      setTimeout(() => {
        window.location.href = item.path;
      }, 600);
    }
  };

  const handlePageChange = () => {
    setIsPageChanging(true);
    setTimeout(() => {
      setIsPageChanging(false);
    }, 1000);
  };

  return (
    <>
      <nav 
        ref={navbarRef}
        className={`navbar ${isScrolled ? 'scrolled' : ''} ${isMobile ? 'mobile' : 'desktop'} ${isPageChanging ? 'page-changing' : ''}`}
      >
        <div className="nav-container">
          {/* Logo/Brand with Page Transition Animations */}
          <motion.div 
            className="nav-brand"
            variants={brandContainerVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            key="nav-brand"
          >
            <AnimatePresence mode="wait">
              {!isPageChanging ? (
                <motion.div 
                  className="brand-container"
                  variants={brandContainerVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  key="brand-container"
                >
                  <motion.div 
                    className="logo-wrapper"
                    variants={logoVariants}
                    whileHover={{
                      scale: 1.05,
                      rotate: -2,
                      transition: { type: "spring", stiffness: 400 }
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img 
                      src={companyLogo} 
                      alt="Meezan Developers Logo" 
                      className="logo-img"
                    />
                  </motion.div>
                  <motion.div 
                    className="brand-text"
                    variants={companyNameVariants}
                  >
                    <motion.span 
                      className="company-name"
                      whileHover={{
                        backgroundPosition: "100% 0%",
                        transition: { duration: 0.8, ease: "easeInOut" }
                      }}
                    >
                      MEEZAN DEVELOPERS
                    </motion.span>
                    {/* <span className="company-tagline">Building Excellence</span> */}
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div 
                  className="brand-container loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key="brand-loading"
                >
                  <motion.div 
                    className="logo-wrapper"
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, 0],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <img 
                      src={companyLogo} 
                      alt="Meezan Developers Logo" 
                      className="logo-img"
                    />
                  </motion.div>
                  <div className="brand-text">
                    <span className="company-name loading">MEEZAN DEVELOPERS</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Desktop Navigation - Only show on desktop */}
          {!isMobile && (
            <motion.div 
              className="nav-menu-container"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ul className="nav-menu desktop-menu">
                {navItems.map((item) => (
                  <motion.li 
                    key={item.name}
                    className={`nav-item ${item.submenu ? 'has-dropdown' : ''}`}
                    ref={item.submenu ? dropdownRef : null}
                    onMouseEnter={() => item.submenu && !isMobile && setServicesDropdown(true)}
                    onMouseLeave={() => item.submenu && !isMobile && setServicesDropdown(false)}
                  >
                    {item.submenu ? (
                      <>
                        <motion.a 
                          href={item.path}
                          className="nav-link dropdown-trigger"
                          whileHover={{ 
                            color: 'var(--primary)'
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => handleNavLinkClick(e, item)}
                        >
                          <span className="nav-link-text">{item.name}</span>
                          <motion.span 
                            className="dropdown-arrow"
                            animate={{ rotate: servicesDropdown ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            ▼
                          </motion.span>
                        </motion.a>
                        
                        <AnimatePresence>
                          {servicesDropdown && (
                            <motion.div 
                              className="dropdown-menu"
                              variants={dropdownVariants}
                              initial="closed"
                              animate="open"
                              exit="closed"
                            >
                              {item.submenu.map((subItem) => (
                                <motion.a
                                  key={subItem.name}
                                  href={subItem.path}
                                  className="dropdown-item"
                                  onClick={() => handleSubmenuClick(subItem.path)}
                                  whileHover={{ 
                                    x: 5,
                                    backgroundColor: 'rgba(255, 180, 0, 0.1)'
                                  }}
                                >
                                  <span className="submenu-icon">{subItem.icon}</span>
                                  <span className="submenu-text">{subItem.name}</span>
                                </motion.a>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <motion.a 
                        href={item.path}
                        className="nav-link"
                        whileHover={{ 
                          color: 'var(--primary)'
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => handleNavLinkClick(e, item)}
                      >
                        <span className="nav-link-text">{item.name}</span>
                      </motion.a>
                    )}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Enhanced Mobile Hamburger Button - Only show on mobile */}
          {isMobile && (
            <motion.button
              className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle navigation menu"
              variants={hamburgerVariants}
              animate={isMenuOpen ? 'open' : 'closed'}
              whileHover={{ 
                scale: 1.1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                variants={line1Variants}
                animate={isMenuOpen ? 'open' : 'closed'}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              />
              <motion.span
                variants={line2Variants}
                animate={isMenuOpen ? 'open' : 'closed'}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              />
              <motion.span
                variants={line3Variants}
                animate={isMenuOpen ? 'open' : 'closed'}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              />
            </motion.button>
          )}

          {/* Enhanced Mobile Navigation Drawer */}
          <AnimatePresence mode="wait">
            {isMenuOpen && isMobile && (
              <>
                {/* Overlay with blur effect */}
                <motion.div
                  className="menu-overlay"
                  variants={overlayVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  onClick={closeMenu}
                />

                {/* Enhanced Drawer Menu */}
                <motion.div
                  className="mobile-drawer"
                  variants={menuVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                >
                  {/* Simplified Drawer Header - Only Close Button */}
                  <div className="drawer-header">
                    <motion.button
                      className="close-drawer"
                      onClick={closeMenu}
                      whileHover={{ 
                        scale: 1.1,
                      }}
                      whileTap={{ scale: 0.9 }}
                      aria-label="Close menu"
                    >
                      <motion.span
                        whileHover={{ rotate: 90 }}
                        transition={{ duration: 0.2 }}
                      >
                        ×
                      </motion.span>
                    </motion.button>
                  </div>

                  {/* Enhanced Navigation Items */}
                  <motion.ul className="drawer-menu">
                    {navItems.map((item, index) => (
                      <motion.li
                        key={item.name}
                        variants={itemVariants}
                        ref={item.submenu ? mobileDropdownRef : null}
                        className={`drawer-item ${item.submenu ? 'has-mobile-dropdown' : ''}`}
                      >
                        {item.submenu ? (
                          <>
                            <motion.div
                              className="drawer-link dropdown-trigger-mobile"
                              onClick={(e) => handleServicesClick(e, true)}
                              whileHover={{ 
                                backgroundColor: 'rgba(255, 180, 0, 0.15)'
                              }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <motion.span 
                                className="nav-icon"
                                whileHover={{ 
                                  scale: 1.3,
                                  rotate: 10 
                                }}
                                transition={{ type: "spring", stiffness: 400 }}
                              >
                                {item.icon}
                              </motion.span>
                              <span className="nav-text">{item.name}</span>
                              <motion.span 
                                className="nav-arrow dropdown-arrow-mobile"
                                animate={{ rotate: mobileServicesDropdown ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                ▼
                              </motion.span>
                            </motion.div>
                            
                            <AnimatePresence>
                              {mobileServicesDropdown && (
                                <motion.div 
                                  className="mobile-dropdown-menu"
                                  variants={mobileDropdownVariants}
                                  initial="closed"
                                  animate="open"
                                  exit="closed"
                                >
                                  {item.submenu.map((subItem) => (
                                    <motion.a
                                      key={subItem.name}
                                      href={subItem.path}
                                      className="mobile-dropdown-item"
                                      onClick={() => handleSubmenuClick(subItem.path)}
                                      whileHover={{ 
                                        x: 8,
                                        backgroundColor: 'rgba(255, 180, 0, 0.1)'
                                      }}
                                      whileTap={{ scale: 0.95 }}
                                    >
                                      <span className="submenu-icon">{subItem.icon}</span>
                                      <span className="submenu-text">{subItem.name}</span>
                                    </motion.a>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </>
                        ) : (
                          <motion.a
                            href={item.path}
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange();
                              setTimeout(() => {
                                window.location.href = item.path;
                                closeMenu();
                              }, 600);
                            }}
                            className="drawer-link"
                            whileHover={{
                              color: 'var(--primary)',
                              backgroundColor: 'rgba(255, 180, 0, 0.15)'
                            }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <motion.span 
                              className="nav-icon"
                              whileHover={{ 
                                scale: 1.3,
                                rotate: 10 
                              }}
                              transition={{ type: "spring", stiffness: 400 }}
                            >
                              {item.icon}
                            </motion.span>
                            <span className="nav-text">{item.name}</span>
                            <motion.span 
                              className="nav-arrow"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.3 + index * 0.1 }}
                              whileHover={{ x: 5 }}
                            >
                              →
                            </motion.span>
                          </motion.a>
                        )}
                      </motion.li>
                    ))}
                  </motion.ul>

                  {/* Mobile CTA Section */}
                  <div className="drawer-footer">
                    <motion.a
                      href="/contact"
                      className="mobile-cta"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange();
                        setTimeout(() => {
                          window.location.href = '/contact';
                          closeMenu();
                        }, 600);
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Get In Touch
                    </motion.a>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
        
        {/* Simple Separation Line */}
        <div className="navbar-separator simple"></div>
      </nav>
      
      {/* Add padding to body to account for fixed navbar */}
      <style jsx>{`
        :global(body) {
          padding-top: 80px;
        }
        @media (max-width: 768px) {
          :global(body) {
            padding-top: 70px;
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;